<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import WhatsappWebhookConfigAPI from 'dashboard/api/whatsappWebhookConfig';
import ButtonV4 from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const getters = useStoreGetters();

const accountId = computed(() => getters.getCurrentAccountId.value);

const loading = ref(false);
const saving = ref(false);
const syncing = ref(false);

const config = ref({
  webhook_app_id: '',
  webhook_app_secret: '',
  webhook_subscribed_fields: [],
});

const subscribedApps = ref([]);
const callbackUrl = ref('');
const verifyToken = ref('');
const webhookStatus = ref(null);
const source = ref('manual'); // 'embedded_signup' or 'manual'

const isEmbeddedSignup = computed(() => source.value === 'embedded_signup');
const isManualConnection = computed(() => source.value === 'manual');

const availableFields = ref([
  {
    value: 'messages',
    label: 'WEBHOOK_FIELDS.MESSAGES',
    description: 'WEBHOOK_FIELDS.MESSAGES_DESC',
  },
  {
    value: 'account_alerts',
    label: 'WEBHOOK_FIELDS.ACCOUNT_ALERTS',
    description: 'WEBHOOK_FIELDS.ACCOUNT_ALERTS_DESC',
  },
  {
    value: 'account_review_update',
    label: 'WEBHOOK_FIELDS.ACCOUNT_REVIEW_UPDATE',
    description: 'WEBHOOK_FIELDS.ACCOUNT_REVIEW_UPDATE_DESC',
  },
  {
    value: 'account_settings_update',
    label: 'WEBHOOK_FIELDS.ACCOUNT_SETTINGS_UPDATE',
    description: 'WEBHOOK_FIELDS.ACCOUNT_SETTINGS_UPDATE_DESC',
  },
  {
    value: 'account_update',
    label: 'WEBHOOK_FIELDS.ACCOUNT_UPDATE',
    description: 'WEBHOOK_FIELDS.ACCOUNT_UPDATE_DESC',
  },
  {
    value: 'automatic_events',
    label: 'WEBHOOK_FIELDS.AUTOMATIC_EVENTS',
    description: 'WEBHOOK_FIELDS.AUTOMATIC_EVENTS_DESC',
  },
  {
    value: 'business_capability_update',
    label: 'WEBHOOK_FIELDS.BUSINESS_CAPABILITY_UPDATE',
    description: 'WEBHOOK_FIELDS.BUSINESS_CAPABILITY_UPDATE_DESC',
  },
  {
    value: 'business_status_update',
    label: 'WEBHOOK_FIELDS.BUSINESS_STATUS_UPDATE',
    description: 'WEBHOOK_FIELDS.BUSINESS_STATUS_UPDATE_DESC',
  },
  {
    value: 'calls',
    label: 'WEBHOOK_FIELDS.CALLS',
    description: 'WEBHOOK_FIELDS.CALLS_DESC',
  },
  {
    value: 'flows',
    label: 'WEBHOOK_FIELDS.FLOWS',
    description: 'WEBHOOK_FIELDS.FLOWS_DESC',
  },
  {
    value: 'group_participants_update',
    label: 'WEBHOOK_FIELDS.GROUP_PARTICIPANTS_UPDATE',
    description: 'WEBHOOK_FIELDS.GROUP_PARTICIPANTS_UPDATE_DESC',
  },
  {
    value: 'group_status_update',
    label: 'WEBHOOK_FIELDS.GROUP_STATUS_UPDATE',
    description: 'WEBHOOK_FIELDS.GROUP_STATUS_UPDATE_DESC',
  },
  {
    value: 'group_lifecycle_update',
    label: 'WEBHOOK_FIELDS.GROUP_LIFECYCLE_UPDATE',
    description: 'WEBHOOK_FIELDS.GROUP_LIFECYCLE_UPDATE_DESC',
  },
  {
    value: 'group_settings_update',
    label: 'WEBHOOK_FIELDS.GROUP_SETTINGS_UPDATE',
    description: 'WEBHOOK_FIELDS.GROUP_SETTINGS_UPDATE_DESC',
  },
  {
    value: 'history',
    label: 'WEBHOOK_FIELDS.HISTORY',
    description: 'WEBHOOK_FIELDS.HISTORY_DESC',
  },
  {
    value: 'message_template_components_update',
    label: 'WEBHOOK_FIELDS.MESSAGE_TEMPLATE_COMPONENTS_UPDATE',
    description: 'WEBHOOK_FIELDS.MESSAGE_TEMPLATE_COMPONENTS_UPDATE_DESC',
  },
  {
    value: 'message_template_status_update',
    label: 'WEBHOOK_FIELDS.MESSAGE_TEMPLATE_STATUS_UPDATE',
    description: 'WEBHOOK_FIELDS.MESSAGE_TEMPLATE_STATUS_UPDATE_DESC',
  },
  {
    value: 'message_template_quality_update',
    label: 'WEBHOOK_FIELDS.MESSAGE_TEMPLATE_QUALITY_UPDATE',
    description: 'WEBHOOK_FIELDS.MESSAGE_TEMPLATE_QUALITY_UPDATE_DESC',
  },
  {
    value: 'partner_solutions',
    label: 'WEBHOOK_FIELDS.PARTNER_SOLUTIONS',
    description: 'WEBHOOK_FIELDS.PARTNER_SOLUTIONS_DESC',
  },
  {
    value: 'payment_configuration_update',
    label: 'WEBHOOK_FIELDS.PAYMENT_CONFIGURATION_UPDATE',
    description: 'WEBHOOK_FIELDS.PAYMENT_CONFIGURATION_UPDATE_DESC',
  },
  {
    value: 'phone_number_name_update',
    label: 'WEBHOOK_FIELDS.PHONE_NUMBER_NAME_UPDATE',
    description: 'WEBHOOK_FIELDS.PHONE_NUMBER_NAME_UPDATE_DESC',
  },
  {
    value: 'phone_number_quality_update',
    label: 'WEBHOOK_FIELDS.PHONE_NUMBER_QUALITY_UPDATE',
    description: 'WEBHOOK_FIELDS.PHONE_NUMBER_QUALITY_UPDATE_DESC',
  },
  {
    value: 'security',
    label: 'WEBHOOK_FIELDS.SECURITY',
    description: 'WEBHOOK_FIELDS.SECURITY_DESC',
  },
  {
    value: 'smb_app_state_sync',
    label: 'WEBHOOK_FIELDS.SMB_APP_STATE_SYNC',
    description: 'WEBHOOK_FIELDS.SMB_APP_STATE_SYNC_DESC',
  },
  {
    value: 'smb_message_echoes',
    label: 'WEBHOOK_FIELDS.SMB_MESSAGE_ECHOES',
    description: 'WEBHOOK_FIELDS.SMB_MESSAGE_ECHOES_DESC',
  },
  {
    value: 'template_category_update',
    label: 'WEBHOOK_FIELDS.TEMPLATE_CATEGORY_UPDATE',
    description: 'WEBHOOK_FIELDS.TEMPLATE_CATEGORY_UPDATE_DESC',
  },
  {
    value: 'template_correct_category_detection',
    label: 'WEBHOOK_FIELDS.TEMPLATE_CORRECT_CATEGORY_DETECTION',
    description: 'WEBHOOK_FIELDS.TEMPLATE_CORRECT_CATEGORY_DETECTION_DESC',
  },
  {
    value: 'tracking_events',
    label: 'WEBHOOK_FIELDS.TRACKING_EVENTS',
    description: 'WEBHOOK_FIELDS.TRACKING_EVENTS_DESC',
  },
  {
    value: 'user_preferences',
    label: 'WEBHOOK_FIELDS.USER_PREFERENCES',
    description: 'WEBHOOK_FIELDS.USER_PREFERENCES_DESC',
  },
]);

const selectedFields = ref([]);

const isFieldSelected = field => selectedFields.value.includes(field);

const toggleField = field => {
  if (isFieldSelected(field)) {
    selectedFields.value = selectedFields.value.filter(f => f !== field);
  } else {
    selectedFields.value.push(field);
  }
};

const selectAll = () => {
  selectedFields.value = availableFields.value.map(f => f.value);
};

const deselectAll = () => {
  selectedFields.value = [];
};

const loadConfig = async () => {
  loading.value = true;
  try {
    const response = await WhatsappWebhookConfigAPI.getConfig(
      accountId.value,
      props.inbox.id
    );

    // Preserve the webhook_app_secret if it was already entered
    const currentSecret = config.value.webhook_app_secret;

    // Try to get secret from localStorage
    const storageKey = `webhook_secret_${props.inbox.id}`;
    const storedSecret = localStorage.getItem(storageKey);

    config.value.webhook_app_id = response.data.webhook_app_id || '';
    config.value.webhook_subscribed_fields =
      response.data.webhook_subscribed_fields || [];

    // Priority order for secret:
    // 1. Currently typed secret (in memory)
    // 2. Secret from localStorage (persisted across reloads)
    // 3. Empty if backend says no secret is saved
    if (currentSecret && currentSecret !== '••••••••••••••••') {
      // Keep what user just typed
      config.value.webhook_app_secret = currentSecret;
    } else if (storedSecret && response.data.webhook_app_secret_present) {
      // Use stored secret if backend confirms it's valid
      config.value.webhook_app_secret = storedSecret;
    } else if (response.data.webhook_app_secret_present) {
      // Backend has a secret but we don't have it locally - show placeholder
      config.value.webhook_app_secret = '••••••••••••••••';
    } else {
      // No secret anywhere
      config.value.webhook_app_secret = '';
    }

    selectedFields.value = response.data.webhook_subscribed_fields || [];
    subscribedApps.value = response.data.subscribed_apps || [];
    callbackUrl.value = response.data.callback_url || '';
    verifyToken.value = response.data.verify_token || '';
    webhookStatus.value = response.data.webhook_status;
    source.value = response.data.source || 'manual';
  } catch (error) {
    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.API.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const saveCredentials = async () => {
  if (!config.value.webhook_app_id || !config.value.webhook_app_secret) {
    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  // Don't allow saving the placeholder
  if (config.value.webhook_app_secret === '••••••••••••••••') {
    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  saving.value = true;
  try {
    await WhatsappWebhookConfigAPI.updateConfig(
      accountId.value,
      props.inbox.id,
      {
        webhook_app_id: config.value.webhook_app_id,
        webhook_app_secret: config.value.webhook_app_secret,
      }
    );

    // Save secret to localStorage for future use
    const storageKey = `webhook_secret_${props.inbox.id}`;
    localStorage.setItem(storageKey, config.value.webhook_app_secret);

    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.API.SAVE_SUCCESS'));
    await loadConfig(); // Reload to get updated info
  } catch (error) {
    // Error handled by alert
    const errorMessage =
      error.response?.data?.error ||
      t('INBOX_MGMT.WEBHOOK_CONFIG.API.SAVE_ERROR');
    useAlert(errorMessage);
  } finally {
    saving.value = false;
  }
};

const syncFromMeta = async () => {
  syncing.value = true;
  try {
    const response = await WhatsappWebhookConfigAPI.fetchSubscribedFields(
      accountId.value,
      props.inbox.id
    );

    selectedFields.value = response.data.subscribed_fields || [];
    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.API.SYNC_SUCCESS'));
  } catch (error) {
    // Error handled by alert
    const errorMessage =
      error.response?.data?.error ||
      t('INBOX_MGMT.WEBHOOK_CONFIG.API.SYNC_ERROR');
    useAlert(errorMessage);
  } finally {
    syncing.value = false;
  }
};

const subscribeFields = async () => {
  if (selectedFields.value.length === 0) {
    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.VALIDATION.NO_FIELDS'));
    return;
  }

  saving.value = true;
  try {
    await WhatsappWebhookConfigAPI.subscribe(
      accountId.value,
      props.inbox.id,
      selectedFields.value
    );

    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.API.SUBSCRIBE_SUCCESS'));
    await loadConfig();
  } catch (error) {
    // Error handled by alert
    const errorMessage =
      error.response?.data?.error ||
      t('INBOX_MGMT.WEBHOOK_CONFIG.API.SUBSCRIBE_ERROR');
    useAlert(errorMessage);
  } finally {
    saving.value = false;
  }
};

const unsubscribeAll = async () => {
  saving.value = true;
  try {
    await WhatsappWebhookConfigAPI.unsubscribe(accountId.value, props.inbox.id);

    selectedFields.value = [];
    useAlert(t('INBOX_MGMT.WEBHOOK_CONFIG.API.UNSUBSCRIBE_SUCCESS'));
    await loadConfig();
  } catch (error) {
    // Error handled by alert
    const errorMessage =
      error.response?.data?.error ||
      t('INBOX_MGMT.WEBHOOK_CONFIG.API.UNSUBSCRIBE_ERROR');
    useAlert(errorMessage);
  } finally {
    saving.value = false;
  }
};

const configureCompleteWebhook = async () => {
  saving.value = true;
  try {
    const response = await WhatsappWebhookConfigAPI.configureComplete(
      accountId.value,
      props.inbox.id
    );

    selectedFields.value = response.data.fields || [];
    useAlert(
      t('INBOX_MGMT.WEBHOOK_CONFIG.API.CONFIGURE_COMPLETE_SUCCESS', {
        total: response.data.total_fields,
      })
    );
    await loadConfig();
  } catch (error) {
    // Error handled by alert
    const errorMessage =
      error.response?.data?.error ||
      t('INBOX_MGMT.WEBHOOK_CONFIG.API.CONFIGURE_COMPLETE_ERROR');
    useAlert(errorMessage);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadConfig();
});
</script>

<template>
  <div class="flex flex-col gap-6 p-6 bg-n-solid-1">
    <!-- Header -->
    <div class="flex flex-col gap-2">
      <div class="flex gap-3 items-center">
        <div
          class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-blue-2"
        >
          <Icon icon="i-lucide-webhook" class="w-5 h-5 text-n-blue-11" />
        </div>
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.DESCRIPTION') }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-8">
      <Icon
        icon="i-lucide-loader-2"
        class="w-6 h-6 animate-spin text-n-blue-11"
      />
    </div>

    <template v-else>
      <!-- Info Badge: Embedded Signup or Manual -->
      <div
        v-if="isEmbeddedSignup"
        class="flex gap-2 items-center p-4 rounded-lg border border-n-green-6 bg-n-green-2"
      >
        <Icon icon="i-lucide-check-circle-2" class="w-5 h-5 text-n-green-11" />
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-green-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EMBEDDED_SIGNUP.BADGE_TITLE') }}
          </span>
          <span class="text-xs text-n-green-11">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EMBEDDED_SIGNUP.BADGE_DESC') }}
          </span>
        </div>
      </div>

      <!-- App Credentials Section (Only for Manual Connections) -->
      <div
        v-if="isManualConnection"
        class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div class="flex gap-2 items-center">
          <Icon icon="i-lucide-key" class="w-4 h-4 text-n-slate-11" />
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.TITLE') }}
          </h3>
        </div>

        <div class="flex flex-col gap-4">
          <div>
            <label class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.APP_ID') }}
              <span class="text-n-ruby-11">*</span>
            </label>
            <p class="mb-2 text-xs text-n-slate-11">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.APP_ID_HELP') }}
            </p>
            <input
              v-model="config.webhook_app_id"
              type="text"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all font-mono"
              :placeholder="
                t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.APP_ID_PLACEHOLDER')
              "
            />
          </div>

          <div>
            <label class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.APP_SECRET') }}
              <span class="text-n-ruby-11">*</span>
            </label>
            <p class="mb-2 text-xs text-n-slate-11">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.APP_SECRET_HELP') }}
            </p>
            <input
              v-model="config.webhook_app_secret"
              type="password"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-blue-8 focus:ring-2 focus:ring-n-blue-3 outline-none transition-all font-mono"
              :placeholder="
                t(
                  'INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.APP_SECRET_PLACEHOLDER'
                )
              "
            />
            <p
              v-if="
                config.webhook_app_secret && config.webhook_app_secret !== ''
              "
              class="mt-1.5 text-xs text-n-green-11 flex gap-1 items-center"
            >
              <Icon icon="i-lucide-check-circle" class="w-3 h-3" />
              <span>{{
                t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.SECRET_SAVED')
              }}</span>
            </p>
          </div>

          <div class="flex gap-3">
            <ButtonV4 small blue :loading="saving" @click="saveCredentials">
              <Icon icon="i-lucide-save" class="w-4 h-4" />
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.CREDENTIALS.SAVE_BUTTON') }}
            </ButtonV4>

            <ButtonV4
              v-if="config.webhook_app_id && config.webhook_app_secret"
              small
              green
              :loading="saving"
              @click="configureCompleteWebhook"
            >
              <Icon icon="i-lucide-zap" class="w-4 h-4" />
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.CONFIGURE_COMPLETE_BUTTON') }}
            </ButtonV4>
          </div>
        </div>
      </div>

      <!-- Webhook Status (for both Embedded and Manual, but different display) -->
      <div
        class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div class="flex gap-2 items-center justify-between">
          <div class="flex gap-2 items-center">
            <Icon icon="i-lucide-activity" class="w-4 h-4 text-n-slate-11" />
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.TITLE') }}
            </h3>
          </div>

          <div v-if="webhookStatus" class="flex gap-2 items-center">
            <div
              v-if="webhookStatus.configured"
              class="flex gap-2 items-center px-3 py-1.5 rounded-full bg-n-green-3 border border-n-green-6"
            >
              <Icon
                icon="i-lucide-check-circle"
                class="w-4 h-4 text-n-green-11"
              />
              <span class="text-xs font-medium text-n-green-12">
                {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.CONFIGURED') }}
              </span>
            </div>
            <div
              v-else
              class="flex gap-2 items-center px-3 py-1.5 rounded-full bg-n-amber-3 border border-n-amber-6"
            >
              <Icon
                icon="i-lucide-alert-circle"
                class="w-4 h-4 text-n-amber-11"
              />
              <span class="text-xs font-medium text-n-amber-12">
                {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.NOT_CONFIGURED') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Expected Callback URL -->
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.EXPECTED_CALLBACK_URL') }}
          </label>
          <div
            class="flex gap-2 items-center p-3 rounded-lg bg-n-solid-1 border border-n-weak"
          >
            <Icon
              icon="i-lucide-link"
              class="w-4 h-4 text-n-slate-10 flex-shrink-0"
            />
            <code class="text-xs font-mono text-n-slate-11 break-all flex-1">
              {{ callbackUrl }}
            </code>
          </div>
          <p class="text-xs text-n-slate-10 mt-1.5">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.EXPECTED_URL_HELP') }}
          </p>
        </div>

        <!-- Meta Configured URL -->
        <div v-if="webhookStatus && webhookStatus.configured">
          <label class="block mb-2 text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.META_CONFIGURED_URL') }}
          </label>
          <div
            class="flex gap-2 items-center p-3 rounded-lg border"
            :class="[
              webhookStatus.callback_url === callbackUrl
                ? 'bg-n-green-2 border-n-green-6'
                : 'bg-n-red-2 border-n-red-6',
            ]"
          >
            <Icon
              :icon="
                webhookStatus.callback_url === callbackUrl
                  ? 'i-lucide-check-circle'
                  : 'i-lucide-alert-circle'
              "
              class="w-4 h-4 flex-shrink-0"
              :class="[
                webhookStatus.callback_url === callbackUrl
                  ? 'text-n-green-11'
                  : 'text-n-red-11',
              ]"
            />
            <code
              class="text-xs font-mono break-all flex-1"
              :class="[
                webhookStatus.callback_url === callbackUrl
                  ? 'text-n-green-12'
                  : 'text-n-red-12',
              ]"
            >
              {{ webhookStatus.callback_url || 'Not configured' }}
            </code>
          </div>
          <p
            v-if="webhookStatus.callback_url !== callbackUrl"
            class="text-xs text-n-red-11 mt-1.5 flex gap-1 items-start"
          >
            <Icon
              icon="i-lucide-alert-triangle"
              class="w-3 h-3 flex-shrink-0 mt-0.5"
            />
            <span>{{
              t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.URL_MISMATCH_WARNING')
            }}</span>
          </p>
        </div>

        <!-- Verify Token -->
        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.VERIFY_TOKEN') }}
          </label>
          <div
            class="flex gap-2 items-center p-3 rounded-lg bg-n-solid-1 border border-n-weak"
          >
            <Icon
              icon="i-lucide-key"
              class="w-4 h-4 text-n-slate-10 flex-shrink-0"
            />
            <code class="text-xs font-mono text-n-slate-11 break-all flex-1">
              {{ verifyToken || 'Not generated yet' }}
            </code>
          </div>
        </div>

        <!-- Status Details -->
        <div
          v-if="webhookStatus && webhookStatus.configured"
          class="flex gap-2 items-start p-3 rounded-lg bg-n-blue-2 border border-n-blue-6"
        >
          <Icon
            icon="i-lucide-info"
            class="flex-shrink-0 w-4 h-4 text-n-blue-11 mt-0.5"
          />
          <div class="flex-1">
            <div class="text-xs font-medium text-n-blue-12">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.CURRENT_CONFIG') }}
            </div>
            <div class="text-xs text-n-blue-11 mt-1">
              {{ webhookStatus.fields_count }}
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.EVENTS_SUBSCRIBED') }}
            </div>
          </div>
        </div>

        <!-- Warning if configured -->
        <div
          v-if="webhookStatus && webhookStatus.configured"
          class="flex gap-2 items-start p-3 rounded-lg bg-n-amber-2 border border-n-amber-6"
        >
          <Icon
            icon="i-lucide-alert-triangle"
            class="flex-shrink-0 w-4 h-4 text-n-amber-11 mt-0.5"
          />
          <div class="flex-1 text-xs text-n-amber-11">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.STATUS.WILL_BE_REPLACED') }}
          </div>
        </div>
      </div>

      <!-- Auto-Configure Info -->
      <div
        v-if="config.webhook_app_id && config.webhook_app_secret"
        class="flex gap-3 p-4 rounded-lg border border-n-green-6 bg-n-green-2"
      >
        <Icon
          icon="i-lucide-zap"
          class="flex-shrink-0 w-5 h-5 text-n-green-11 mt-0.5"
        />
        <div class="flex-1">
          <div class="text-sm font-medium text-n-green-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.AUTO_CONFIGURE.TITLE') }}
          </div>
          <div class="text-sm text-n-green-11 mt-1">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.AUTO_CONFIGURE.MESSAGE') }}
          </div>
          <div class="flex gap-2 items-center mt-3 p-2 rounded bg-n-green-3">
            <Icon icon="i-lucide-link" class="w-4 h-4 text-n-green-11" />
            <code class="text-xs font-mono text-n-green-12">
              {{ callbackUrl }}
            </code>
          </div>
        </div>
      </div>

      <!-- Subscribed Apps Info -->
      <div
        v-if="subscribedApps.length > 0"
        class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div class="flex gap-2 items-center">
          <Icon icon="i-lucide-box" class="w-4 h-4 text-n-slate-11" />
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.APPS.TITLE') }}
          </h3>
        </div>

        <div class="flex flex-col gap-3">
          <div
            v-for="app in subscribedApps"
            :key="app.app_id"
            class="flex gap-3 items-start p-3 rounded-lg border border-n-weak bg-n-solid-1"
          >
            <div class="flex-1">
              <div class="flex gap-2 items-center">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WEBHOOK_CONFIG.APPS.APP_ID') }}:
                </span>
                <code
                  class="text-xs font-mono text-n-blue-11 bg-n-blue-2 px-2 py-0.5 rounded"
                >
                  {{ app.app_id }}
                </code>
              </div>
              <div class="flex gap-1 items-center mt-2 text-xs text-n-slate-10">
                <Icon icon="i-lucide-info" class="w-3 h-3" />
                <span>
                  {{ app.subscribed_fields.length }}
                  {{
                    t('INBOX_MGMT.WEBHOOK_CONFIG.APPS.SUBSCRIBED_FIELDS_COUNT')
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex gap-2 items-start p-3 rounded-lg bg-n-amber-2 border border-n-amber-6"
        >
          <Icon
            icon="i-lucide-alert-triangle"
            class="flex-shrink-0 w-4 h-4 text-n-amber-11 mt-0.5"
          />
          <p class="text-xs text-n-amber-11">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.APPS.INFO') }}
          </p>
        </div>
      </div>

      <!-- Webhook Events Section -->
      <div
        class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div class="flex gap-2 items-center justify-between">
          <div class="flex gap-2 items-center">
            <Icon icon="i-lucide-bell" class="w-4 h-4 text-n-slate-11" />
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EVENTS.TITLE') }}
            </h3>
          </div>

          <!-- Action buttons only for Manual connections -->
          <div v-if="isManualConnection" class="flex gap-2">
            <ButtonV4 xs ghost blue :loading="syncing" @click="syncFromMeta">
              <Icon icon="i-lucide-refresh-cw" class="w-3.5 h-3.5" />
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EVENTS.SYNC_BUTTON') }}
            </ButtonV4>

            <ButtonV4 xs ghost @click="selectAll">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EVENTS.SELECT_ALL') }}
            </ButtonV4>

            <ButtonV4 xs ghost @click="deselectAll">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EVENTS.DESELECT_ALL') }}
            </ButtonV4>
          </div>

          <!-- Info badge for Embedded Signup -->
          <div
            v-if="isEmbeddedSignup"
            class="flex gap-2 items-center px-3 py-1.5 rounded-full bg-n-blue-3 border border-n-blue-6"
          >
            <Icon icon="i-lucide-lock" class="w-3.5 h-3.5 text-n-blue-11" />
            <span class="text-xs font-medium text-n-blue-12">
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EMBEDDED_SIGNUP.READ_ONLY') }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-3">
          <div
            v-for="field in availableFields"
            :key="field.value"
            class="flex gap-3 items-start p-3 rounded-lg border transition-all"
            :class="[
              isFieldSelected(field.value)
                ? 'border-n-blue-8 bg-n-blue-2'
                : 'border-n-weak bg-n-solid-1',
              isManualConnection
                ? 'cursor-pointer hover:border-n-blue-6'
                : 'opacity-75',
            ]"
            @click="isManualConnection && toggleField(field.value)"
          >
            <div class="flex-shrink-0 mt-0.5">
              <div
                class="w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
                :class="[
                  isFieldSelected(field.value)
                    ? 'border-n-blue-9 bg-n-blue-9'
                    : 'border-n-slate-8 bg-n-solid-1',
                ]"
              >
                <Icon
                  v-if="isFieldSelected(field.value)"
                  icon="i-lucide-check"
                  class="w-3 h-3 text-white"
                />
              </div>
            </div>

            <div class="flex-1">
              <div class="text-sm font-medium text-n-slate-12">
                {{ t(`INBOX_MGMT.WEBHOOK_CONFIG.${field.label}`) }}
              </div>
              <div class="text-xs text-n-slate-11 mt-1">
                {{ t(`INBOX_MGMT.WEBHOOK_CONFIG.${field.description}`) }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex gap-3 items-center justify-between mt-2">
          <div class="text-sm text-n-slate-11">
            {{ selectedFields.length }}
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EVENTS.SELECTED_COUNT') }}
          </div>

          <!-- Action buttons only for Manual connections -->
          <div v-if="isManualConnection" class="flex gap-2">
            <ButtonV4
              small
              ghost
              ruby
              :disabled="selectedFields.length === 0"
              :loading="saving"
              @click="unsubscribeAll"
            >
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EVENTS.UNSUBSCRIBE_ALL') }}
            </ButtonV4>

            <ButtonV4
              small
              blue
              :disabled="selectedFields.length === 0"
              :loading="saving"
              @click="subscribeFields"
            >
              <Icon icon="i-lucide-check-circle" class="w-4 h-4" />
              {{ t('INBOX_MGMT.WEBHOOK_CONFIG.EVENTS.APPLY_BUTTON') }}
            </ButtonV4>
          </div>
        </div>
      </div>

      <!-- Info Alert -->
      <div class="flex gap-3 p-4 rounded-lg border border-n-blue-6 bg-n-blue-2">
        <Icon
          icon="i-lucide-info"
          class="flex-shrink-0 w-5 h-5 text-n-blue-11 mt-0.5"
        />
        <div class="flex-1">
          <div class="text-sm font-medium text-n-blue-12">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.INFO.TITLE') }}
          </div>
          <div class="text-sm text-n-blue-11 mt-1">
            {{ t('INBOX_MGMT.WEBHOOK_CONFIG.INFO.MESSAGE') }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
